import { Cancel, CheckCircle, Close, Update } from '@mui/icons-material';
import { Chip, Stack, Typography } from '@mui/material';
import type { IFeatureFlags } from '@sharedInterfaces/IFeatureFlags';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import packageJson from '@src/../package.json';
import Button from '@src/App/Components/Buttons/Button/Button';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { stage } from '@src/globals';
import { setCompanyFeatureFlags } from '@store/reducer/companyReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from '../Dialog/Dialog';

const possibleStatuses = ['registered', 'installing', 'waiting', 'active', 'redundant'];

export default function VersionDialogButton() {
    const { openDialog } = useDialog();
    const width = useSelector((state: AppState) => state.windowSize.width);

    return (
        <Typography
            variant="caption"
            component="span"
            onClick={() => {
                openDialog(<VersionDialog id="VersionDialog" />);
            }}
            sx={{ cursor: 'pointer' }}
        >
            {`${width > 500 ? 'Version' : 'V'} ${packageJson.version}`}
        </Typography>
    );
}

interface VersionDialogProps {
    id: string;
    resolve?: () => void;
}

// Feature flag configuration
const FEATURE_FLAG_CONFIG: Array<{ key: keyof IFeatureFlags; labelKey: string }> = [
    { key: 'organizations', labelKey: 'organizations' },
    { key: 'employees', labelKey: 'employees' },
    { key: 'employeeTalks', labelKey: 'employeeTalks' },
    { key: 'messages', labelKey: 'messages' },
    { key: 'opportunities', labelKey: 'opportunities' },
    { key: 'landingPage', labelKey: 'landingPage' },
    { key: 'game', labelKey: 'game' },
];

function VersionDialog({ id, resolve }: VersionDialogProps) {
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).layout;
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    const toggleFeatureFlag = (flag: keyof IFeatureFlags) => {
        dispatch(
            setCompanyFeatureFlags({
                ...featureFlags,
                [flag]: !featureFlags[flag],
            }),
        );
    };

    const getFeatureFlagLabel = (key: keyof IFeatureFlags): string => {
        const labels: Record<keyof IFeatureFlags, string> = {
            organizations: 'Organisationen',
            employees: 'Mitarbeiter',
            employeeTalks: 'Mitarbeitergespräche',
            messages: 'Nachrichten',
            opportunities: 'Anfragen',
            landingPage: 'Landingpage',
            game: 'Gamification',
        };
        return labels[key];
    };

    const renderFeatureChip = ({ key }: { key: keyof IFeatureFlags; labelKey: string }) => (
        <Chip
            key={key}
            label={getFeatureFlagLabel(key)}
            color={featureFlags[key] ? 'success' : 'default'}
            icon={featureFlags[key] ? <CheckCircle /> : <Cancel />}
            size="small"
            onClick={() => toggleFeatureFlag(key)}
            sx={{ cursor: 'pointer' }}
        />
    );

    const [support, setSupport] = useState(false);
    const [status, setStatus] = useState(langStrings.checking);
    const [scope, setScope] = useState('');
    const [clientsCount, setClientsCount] = useState(0);
    const [version] = useState(packageJson.version);
    const [cacheVersion, setCacheVersion] = useState(null);

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            setSupport(true);
            navigator.serviceWorker
                .getRegistration()
                .then((reg) => {
                    if (reg) {
                        setStatus(reg.active?.state ?? langStrings.oftenUsed.unknown);
                        setScope(reg.scope);

                        navigator.serviceWorker
                            .getRegistrations()
                            .then((regs) => {
                                setClientsCount(regs.filter((r) => r.scope).length);
                            })
                            .catch(() => setClientsCount(0));

                        if (reg.active) {
                            const messageChannel = new MessageChannel();
                            messageChannel.port1.onmessage = (event) => {
                                if (event.data.type === 'CACHE_VERSION') {
                                    setCacheVersion(event.data.cacheVersion);
                                }
                            };
                            reg.active.postMessage({ type: 'GET_CACHE_VERSION' }, [messageChannel.port2]);
                        }
                    }
                })
                .catch(() => {
                    setStatus(langStrings.oftenUsed.unknown);
                });
        }
    }, [langStrings]);

    // Informationen über den Browser
    const browserInfo = `
    App Name: ${navigator.appName}
    App Version: ${navigator.appVersion}
    User Agent: ${navigator.userAgent}
  `;
    const onClose = () => {
        resolve && resolve();
    };
    return (
        <Dialog
            id={id}
            onClose={onClose}
            title={langStrings.versionDialog}
            footer={<Button size={'small'} text={langStrings.oftenUsed.close} icon={<Close />} onClick={onClose} />}
        >
            <Typography variant="h6" gutterBottom>
                App-Version: {version}
            </Typography>
            <Typography variant="h6" gutterBottom>
                Cache-Version: {cacheVersion}
            </Typography>
            <Typography variant="h6" gutterBottom>
                {langStrings.stage}: {stage}
            </Typography>

            <Typography variant="h6" gutterBottom>
                Service Worker
            </Typography>
            <Typography variant="body1">
                Service Worker Support: {support ? langStrings.oftenUsed.yes : langStrings.oftenUsed.no}
            </Typography>
            <Typography
                variant="body1"
                title={`${langStrings.possibleServiceWorkerStatuses}: ${possibleStatuses.join(', ')}`}
            >
                {langStrings.currentStatus}: {status}
            </Typography>
            <Typography variant="body1">
                {langStrings.scope}: {scope}
            </Typography>
            <Typography variant="body1">
                {langStrings.numberOfConnectedClients}: {clientsCount}
            </Typography>

            <Button
                size={'normal'}
                text={'Check for Update'}
                icon={<Update />}
                onClick={singleUpdateCheck.bind(null, true)}
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Feature Flags
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {FEATURE_FLAG_CONFIG.map(renderFeatureChip)}
            </Stack>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {langStrings.browserInformations}
            </Typography>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {browserInfo}
            </Typography>
        </Dialog>
    );
}

const singleUpdateCheck = async (clearCache: boolean = false) => {
    try {
        if (clearCache && 'caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        }

        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                await registration.update();
                const waitingWorker = registration.waiting;
                if (waitingWorker) {
                    navigator.serviceWorker.addEventListener('controllerchange', () => {
                        window.location.reload();
                    });
                    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
                }
            }
        }
    } catch (error) {
        console.error('Service worker update failed:', error);
    }
};

let updateTimeouts: NodeJS.Timeout[] = [];

export const checkForServiceWorkerUpdate = () => {
    // Clear existing timeouts
    updateTimeouts.forEach(clearTimeout);
    updateTimeouts = [];

    // Set new timeouts
    updateTimeouts.push(
        setTimeout(singleUpdateCheck, 2500),
        setTimeout(singleUpdateCheck, 5000),
        setTimeout(singleUpdateCheck, 10000),
    );
};
